import { User } from "../domain/types/auth.types";
import { httpService } from "./http.service";

export class ProfileService {
  /**
   * Fetch user profile data by userId
   * @param userId - The unique identifier for the user
   * @returns Promise<User> - User profile information
   */
  static async getUserProfile(userId: string): Promise<User> {
    try {
      const response = await httpService.get<{
        user: {
          id: string;
          username: string;
          email: string;
          firstName: string;
          lastName: string;
          phoneNumber: string | null;
          emailVerified: boolean;
          phoneVerified: boolean;
          mfaEnabled: boolean;
          createdAt: string;
          roles: string[];
        };
        companyRole: string;
        organisationName: string;
      }>(`/api/profiles/${userId}`);

      // Transform API response to User format
      return {
        id: response.user.id,
        email: response.user.email,
        firstName: response.user.firstName,
        lastName: response.user.lastName,
        phone: response.user.phoneNumber || undefined,
        phoneVerified: response.user.phoneVerified,
        companyRole: response.companyRole || undefined,
        organisationName: response.organisationName || undefined,
        mfaRequired: response.user.mfaEnabled, // mfaRequired means MFA is NOT enabled yet
        username: response.user.username,
        roles: response.user.roles,
        createdAt: new Date(response.user.createdAt),
      };
    } catch (error) {
      console.error("Failed to fetch user profile:", error);
      throw error;
    }
  }

  /**
   * Store user data in localStorage (for profile data caching)
   * @param user - User data to store
   */
  static saveUserProfile(user: User): void {
    localStorage.setItem("userProfile", JSON.stringify(user));
  }

  /**
   * Get user data from localStorage (profile data cache)
   * @returns User | null - Stored user data or null if not found
   */
  static getUserProfileFromStorage(): User | null {
    const profileStr = localStorage.getItem("userProfile");
    return profileStr ? JSON.parse(profileStr) : null;
  }

  /**
   * Clear user profile data from localStorage
   */
  static clearUserProfile(): void {
    localStorage.removeItem("userProfile");
  }

  /**
   * Update user profile data
   * @param userId - The unique identifier for the user
   * @param profileData - The profile data to update
   * @returns Promise<User> - Updated user profile information
   */
  static async updateUserProfile(
    userId: string,
    profileData: {
      firstName?: string;
      lastName?: string;
      companyRole?: string;
      organisationName?: string;
    }
  ): Promise<User> {
    try {
      const response = await httpService.patch<{
        user: {
          id: string;
          username: string;
          email: string;
          firstName: string;
          lastName: string;
          phoneNumber: string | null;
          emailVerified: boolean;
          phoneVerified: boolean;
          mfaEnabled: boolean;
          createdAt: string;
          roles: string[];
        };
        companyRole: string;
        organisationName: string;
      }>(`/api/profiles/${userId}`, profileData);

      // Transform API response to User format
      return {
        id: response.user.id,
        email: response.user.email,
        firstName: response.user.firstName,
        lastName: response.user.lastName,
        phone: response.user.phoneNumber || undefined,
        phoneVerified: response.user.phoneVerified,
        companyRole: response.companyRole || undefined,
        organisationName: response.organisationName || undefined,
        mfaRequired: response.user.mfaEnabled,
        username: response.user.username,
        roles: response.user.roles,
        createdAt: new Date(response.user.createdAt),
      };
    } catch (error) {
      console.error("Failed to update user profile:", error);
      throw error;
    }
  }
}
